import { TPasskeyItem } from "@/api/services/main/auth/dtos/passkey.dto";
import { IconEdit, IconTrash } from "@/assets/icons";
import { formatDate } from "@/components/commons/PasskeyManager/utils";
import { LoadingArea } from "@/components/elements/Loading/Loading";
import { black, divider, text } from "@/theme/colors";
import { Stack, Typography } from "@mui/material";
import { FC, ReactNode } from "react";

type TPasskeyListProps = {
  passkeys: TPasskeyItem[];
  isLoading: boolean;
  onRename: (passkey: TPasskeyItem) => void;
  onDelete: (passkey: TPasskeyItem) => void;
};

export const PasskeyList: FC<TPasskeyListProps> = ({ passkeys, isLoading, onRename, onDelete }) => {
  if (isLoading) return <LoadingArea height={120} />;

  if (passkeys.length === 0) {
    return (
      <Typography variant="body14" color={text.secondary}>
        パスキーが登録されていません。「新規追加」から登録すると、次回からパスワードなしでログインできます。
      </Typography>
    );
  }

  return (
    <Stack gap="20px">
      {passkeys.map((passkey) => (
        <Stack key={passkey.id} gap="8px" pb={1} borderBottom="1px solid" borderColor={divider.middle} style={{ wordBreak: "break-all" }}>
          <Typography variant="sub16Semi" color={text.primary}>
            {passkey.deviceName || "パスキー"}
          </Typography>
          <Stack direction="row" flexWrap="wrap" columnGap="24px" rowGap="4px">
            <Typography variant="body14" color={text.primary}>
              追加登録日: {formatDate(passkey.createdAt)}
            </Typography>
            <Typography variant="body14" color={text.primary}>
              最終使用日: {formatDate(passkey.lastUsedAt)}
            </Typography>
          </Stack>
          <Stack direction="row" alignItems="center" gap="24px">
            <PasskeyAction
              icon={<IconEdit fontSize={20} color={black[80]} />}
              label="名前を変更"
              color={text.primary}
              onClick={() => onRename(passkey)}
            />
            <PasskeyAction icon={<IconTrash fontSize={20} />} label="パスキーを削除" color={text.error_light} onClick={() => onDelete(passkey)} />
          </Stack>
        </Stack>
      ))}
    </Stack>
  );
};

type TPasskeyActionProps = {
  icon: ReactNode;
  label: string;
  color: string;
  onClick: () => void;
};

const PasskeyAction: FC<TPasskeyActionProps> = ({ icon, label, color, onClick }) => (
  <Stack direction="row" alignItems="center" gap="4px" onClick={onClick} sx={{ cursor: "pointer", color }}>
    {icon}
    <Typography variant="cap12" color={color}>
      {label}
    </Typography>
  </Stack>
);
